import cn from 'classnames';
import { Fragment, useCallback } from 'react';

import { Loader } from './Loader';
import s from './MobileList.module.css';

interface IMobileListProps<TEntity> {
  data: TEntity[];
  renderItem: (entity: TEntity, idx: number) => React.ReactNode;
  getEntityId: (entity: TEntity) => string;
  onClickRow?: (entity: TEntity | undefined) => void;
  getEntityGroup?: (entity: TEntity) => string;
  groupOrder?: string[];
  translateGroupName?: (groupName: string) => React.ReactNode;
  isLoading?: boolean;
  action?: {
    title: React.ReactNode;
    onClick: () => void;
  };
}

const MobileList = <TEntity,>({
  action,
  data,
  getEntityId,
  onClickRow,
  renderItem,
  getEntityGroup,
  groupOrder,
  translateGroupName = (str: string) => str,
  isLoading = false,
}: IMobileListProps<TEntity>) => {
  const handleClickRow = useCallback(
    (event: React.MouseEvent<HTMLLIElement>) => {
      if (!onClickRow) {
        return;
      }

      const id = event.currentTarget.dataset.entityId ?? '';
      const entity = data.find(item => getEntityId(item) === id);

      onClickRow(entity);
    },
    [data, getEntityId, onClickRow],
  );

  if (!getEntityGroup || !groupOrder) {
    return (
      <ul className={s.list}>
        {isLoading && <Loader />}
        {data.map((item, idx) => (
          <li
            key={idx}
            className={cn(s.item, { [s.clickable]: onClickRow })}
            onClick={handleClickRow}
            data-entity-id={getEntityId(item)}
          >
            {renderItem(item, idx)}
          </li>
        ))}
      </ul>
    );
  }

  const grouped =
    getEntityGroup &&
    data.reduce((result: Record<string, TEntity[]>, entity: TEntity) => {
      const group = getEntityGroup(entity);

      result[group] = result[group] ?? [];
      result[group].push(entity);

      return result;
    }, {});

  let isActionRendered = false;

  return (
    <ul className={s.list}>
      {isLoading && <Loader />}
      {groupOrder.map(groupName => {
        if (!grouped[groupName]) {
          return null;
        }

        return (
          <Fragment key={groupName}>
            <div className={s.groupRow}>
              <h6 className={s.group}>{translateGroupName(groupName)}</h6>
              {(() => {
                if (!action || isActionRendered) {
                  return null;
                }

                isActionRendered = true;

                return (
                  <button className={s.action} onClick={action.onClick}>
                    {action.title}
                  </button>
                );
              })()}
            </div>
            {grouped[groupName]?.map((item, idx) => (
              <li
                key={`${groupName}-${idx}`}
                className={cn(s.item, { [s.clickable]: onClickRow })}
                onClick={handleClickRow}
                data-entity-id={getEntityId(item)}
              >
                {renderItem(item, idx)}
              </li>
            ))}
          </Fragment>
        );
      })}
    </ul>
  );
};

export type { IMobileListProps };
export { MobileList };
